"use client";

import { useAppContext } from "@/context/AppContext";
import type { UserActivity } from "@/types";
import { fetchUserActivity } from "@/utils";
import { buildAuthPayload } from "@/utils/MyEstateHelper";
import { useEffect, useState } from "react";

const USER_ACTIVITY_UPDATED_EVENT = "softiq:user-activity-updated";

export function notifyUserActivityUpdated() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(USER_ACTIVITY_UPDATED_EVENT));
  }
}

export function useUserActivity() {
  const { isLoggedIn, userInfoGlobal } = useAppContext();
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [refreshIndex, setRefreshIndex] = useState(0);

  useEffect(() => {
    const handleActivityUpdate = () =>
      setRefreshIndex((current) => current + 1);
    window.addEventListener(USER_ACTIVITY_UPDATED_EVENT, handleActivityUpdate);
    return () =>
      window.removeEventListener(
        USER_ACTIVITY_UPDATED_EVENT,
        handleActivityUpdate,
      );
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !userInfoGlobal?.token) {
      setActivities([]);
      setHasLoaded(true);
      setHasError(false);
      return;
    }

    let isActive = true;
    setIsLoading(true);
    setHasLoaded(false);
    setHasError(false);
    const payload = buildAuthPayload(
      userInfoGlobal.email,
      userInfoGlobal.token,
      {},
    );
    void fetchUserActivity(payload, userInfoGlobal.token)
      .then((response) => {
        if (!isActive) return;
        const nextActivities = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        setActivities(nextActivities);
      })
      .catch(() => {
        if (isActive) {
          setActivities([]);
          setHasError(true);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
          setHasLoaded(true);
        }
      });

    return () => {
      isActive = false;
    };
  }, [isLoggedIn, refreshIndex, userInfoGlobal?.email, userInfoGlobal?.token]);

  const refresh = () => setRefreshIndex((current) => current + 1);

  return { activities, isLoading, hasLoaded, hasError, refresh };
}
